import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  ArrowBack,
  Person,
  CalendarToday,
  Article,
  YouTube,
} from "@mui/icons-material";
import { getApiUrl } from "../config/api";

export const MinistracacoDetalhes = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [ministracao, setMinistracao] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMinistracao = async () => {
      try {
        const response = await fetch(getApiUrl("/api/ministracoes"));
        const data = await response.json();
        if (data.success) {
          const found = data.data.find((m) => m.id === id);
          setMinistracao(found || null);
        }
      } catch (error) {
        console.error("Erro ao buscar ministração:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchMinistracao();
  }, [id]);

  if (loading) {
    return (
      <Box
        sx={{
          backgroundColor: "#f8f9fa",
          minHeight: "100vh",
          pt: { xs: "80px", md: "100px" },
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress sx={{ color: "primary.main" }} />
      </Box>
    );
  }

  if (!ministracao) {
    return (
      <Box
        sx={{
          backgroundColor: "#f8f9fa",
          minHeight: "100vh",
          pt: { xs: "80px", md: "100px" },
          pb: { xs: 4, md: 6 },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h4" color="error" gutterBottom>
              Ministração não encontrada
            </Typography>
            <Button
              variant="contained"
              startIcon={<ArrowBack />}
              onClick={() => navigate("/ministracoes")}
              sx={{
                backgroundColor: "primary.main",
                "&:hover": {
                  backgroundColor: "primary.dark",
                },
              }}
            >
              Voltar às Ministrações
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    });
  };

  const formatContent = (content) => {
    return content.split("\n").map(
      (paragraph, index) =>
        paragraph.trim() && (
          <Typography
            key={index}
            variant="body1"
            paragraph
            sx={{
              lineHeight: 1.8,
              fontSize: { xs: "1rem", md: "1.1rem" },
              color: "#333",
              textAlign: "justify",
            }}
          >
            {paragraph.trim()}
          </Typography>
        ),
    );
  };

  return (
    <Box
      sx={{
        backgroundColor: "#f8f9fa",
        minHeight: "100vh",
        pt: { xs: "80px", md: "100px" },
        pb: { xs: 4, md: 6 },
      }}
    >
      <Container maxWidth="md">
        {/* Botão Voltar */}
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/ministracoes")}
          sx={{
            color: "primary.main",
            fontWeight: "bold",
            textTransform: "none",
            mb: 3,
            "&:hover": {
              backgroundColor: "rgba(139, 38, 53, 0.04)",
            },
          }}
        >
          Voltar às Ministrações
        </Button>

        {/* Conteúdo Principal */}
        <Paper
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          }}
        >
          {/* Imagem de Destaque */}
          <Box
            component="img"
            src={ministracao.imagem}
            alt={ministracao.titulo}
            sx={{
              width: "100%",
              height: { xs: 250, md: 350 },
              objectFit: "cover",
              backgroundColor: "#f0f0f0",
            }}
            onError={(e) => {
              e.target.src = `data:image/svg+xml;base64,${btoa(`
                <svg width="800" height="400" xmlns="http://www.w3.org/2000/svg">
                  <rect width="100%" height="100%" fill="#8B2635"/>
                  <text x="50%" y="50%" font-family="Arial, sans-serif" font-size="24" 
                        fill="white" text-anchor="middle" dy="0.3em">
                    Imagem não encontrada
                  </text>
                </svg>
              `)}`;
            }}
          />

          {/* Conteúdo */}
          <Box sx={{ p: { xs: 3, md: 4 } }}>
            {/* Meta informações */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 2, md: 3 },
                mb: 3,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CalendarToday sx={{ fontSize: 18, color: "primary.main" }} />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: "500" }}
                >
                  {formatDate(ministracao.data)}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Person sx={{ fontSize: 18, color: "primary.main" }} />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: "500" }}
                >
                  {ministracao.autor || "Igreja Verbo da Vida"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Article sx={{ fontSize: 18, color: "primary.main" }} />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: "500" }}
                >
                  Ministração
                </Typography>
              </Box>
            </Box>

            {/* Título */}
            <Typography
              variant="h4"
              component="h1"
              sx={{
                color: "primary.main",
                fontWeight: "bold",
                mb: 3,
                fontSize: { xs: "1.75rem", md: "2.125rem" },
                lineHeight: 1.3,
              }}
            >
              {ministracao.titulo}
            </Typography>

            {/* Resumo destacado */}
            <Paper
              sx={{
                backgroundColor: "rgba(139, 38, 53, 0.05)",
                border: "1px solid rgba(139, 38, 53, 0.1)",
                p: 3,
                mb: 4,
                borderRadius: 2,
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontStyle: "italic",
                  color: "primary.main",
                  fontWeight: "500",
                  lineHeight: 1.6,
                  fontSize: { xs: "1rem", md: "1.1rem" },
                }}
              >
                {ministracao.conteudo}
              </Typography>
            </Paper>

            {/* Vídeo do YouTube */}
            {ministracao.youtubeVideoId && (
              <Box sx={{ mt: 3, mb: 4 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
                >
                  <YouTube sx={{ color: "#FF0000" }} />
                  <Typography variant="h6" fontWeight="bold" color="#333">
                    Assista ao Culto
                  </Typography>
                </Box>
                <Box
                  sx={{
                    position: "relative",
                    paddingBottom: "56.25%",
                    height: 0,
                    overflow: "hidden",
                    borderRadius: 2,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  }}
                >
                  <iframe
                    src={`https://www.youtube.com/embed/${ministracao.youtubeVideoId}`}
                    title={ministracao.titulo}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      border: "none",
                    }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </Box>
              </Box>
            )}

            {/* Conteúdo completo */}
            {ministracao.conteudo && (
              <Box sx={{ mt: 4 }}>{formatContent(ministracao.conteudo)}</Box>
            )}

            {/* Botão de ação final */}
            <Box sx={{ textAlign: "center", mt: 5 }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<ArrowBack />}
                onClick={() => navigate("/ministracoes")}
                sx={{
                  backgroundColor: "primary.main",
                  color: "white",
                  fontWeight: "bold",
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  "&:hover": {
                    backgroundColor: "primary.dark",
                  },
                }}
              >
                Ver Mais Ministrações
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};
